class BYOBBuilder extends HTMLElement {
  constructor() {
    super();

    // 1. Get configurations from data attributes (Safe to run in constructor)
    this.smallBoxVariantId = this.dataset.smallBoxId;
    this.mediumBoxVariantId = this.dataset.mediumBoxId;
    this.largeBoxVariantId = this.dataset.largeBoxId;
    
    this.giftWrapVariantId = this.dataset.giftWrapId;
    this.greetingCardVariantId = this.dataset.greetingCardId;
    this.giftWrapPrice = parseInt(this.dataset.giftWrapPrice);
    this.greetingCardPrice = parseInt(this.dataset.greetingCardPrice);

    // Initial Active State
    this.state = {
      boxCapacity: 6,
      boxName: "Small Box (6 Pieces)",
      selectedItems: [], // Flat selected chiki objects
      addCard: false,
      cardName: "No Card",
      addWrap: false,
      occasion: "None",
      message: "",
      isFullCelebrated: false
    };
  }

  // NATIVE LIFECYCLE: Fires when the element is fully mounted in the page's DOM
  connectedCallback() {
    this.init();
  }

  init() {
    this.setupBoxSelectors();
    this.setupTabs();
    this.setupQuantityControls();
    this.setupPersonalization();
    this.renderSlots();
    this.renderSidebarList();
    this.updateTotals();
    this.injectLoadingOverlay(); // Dynamic injection of the UI-blocking overlay element
    this.setupFormSubmission();
  }

  // Helper: Dynamically creates and appends the loading backdrop to the body
  injectLoadingOverlay() {
    if (document.getElementById('byob-loading-overlay')) return;

    const overlay = document.createElement('div');
    overlay.id = 'byob-loading-overlay';
    overlay.className = 'byob-overlay';
    overlay.innerHTML = `
      <div class="byob-overlay-spinner"></div>
      <p style="font-size: 1.6rem; font-weight: bold; margin: 0; font-family: inherit;">Building Your Custom Gift Box...</p>
    `;
    document.body.appendChild(overlay);
  }

  // Helper: Get the correct Shopify variant ID based on current selection size
  getBoxVariantId() {
    const capacity = this.state.boxCapacity;
    if (capacity === 6) return this.smallBoxVariantId;
    if (capacity === 12) return this.mediumBoxVariantId;
    if (capacity === 18) return this.largeBoxVariantId;
    return null;
  }

  // STEP 1: Handlers for Box size selection cards + Auto-Scroll to Step 2
  setupBoxSelectors() {
    const cards = this.querySelectorAll('.byob-box-card');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');

        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;

        this.state.boxCapacity = parseInt(radio ? radio.value : 6);
        this.state.boxName = radio ? radio.dataset.name : "Small Box (6 Pieces)";
        this.state.isFullCelebrated = false;

        if (this.state.selectedItems.length > this.state.boxCapacity) {
          this.state.selectedItems = this.state.selectedItems.slice(0, this.state.boxCapacity);
          this.syncQuantitiesToDOM();
        }

        this.renderSlots();
        this.renderSidebarList();
        this.updateTotals();
        this.updateProgressTracker();

        // Trigger 1: Auto-smooth scroll to Step 2 Heading (clearing sticky headers)
        setTimeout(() => {
          this.smoothScrollToStep(2);
        }, 200);
      });
    });
  }

  // STEP 2: Category Tab switching logic
  setupTabs() {
    const tabButtons = this.querySelectorAll('.byob-tab-btn');
    const panels = this.querySelectorAll('.byob-tab-panel');

    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        tabButtons.forEach(b => b.classList.remove('is-active'));
        panels.forEach(p => p.classList.remove('is-active'));

        btn.classList.add('is-active');
        const index = btn.dataset.tabIndex;
        const targetPanel = this.querySelector(`#byob-panel-${index}`);
        if (targetPanel) targetPanel.classList.add('is-active');
      });
    });
  }

  // STEP 2: Chiki Quantity Increment and Decrement bindings
  setupQuantityControls() {
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('plus-btn')) {
        const card = e.target.closest('.chikki-card');
        this.addChikki(card);
      }
      if (e.target.classList.contains('minus-btn')) {
        const card = e.target.closest('.chikki-card');
        this.removeChikki(card);
      }
    });
  }

  // STEP 3: Personalization visual grid selections
  setupPersonalization() {
    // 1. Occasion Button Click Listeners (Visual Icon grid selection)
    const occasionBtns = this.querySelectorAll('.byob-occasion-btn');
    occasionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        occasionBtns.forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.state.occasion = btn.dataset.occasion;
        this.updateTotals();
      });
    });

    // 2. Greeting Card Choice Card Clicks (Visual Card Grid)
    const cardChoices = this.querySelectorAll('.byob-card-choice');
    cardChoices.forEach(choice => {
      choice.addEventListener('click', () => {
        cardChoices.forEach(c => c.classList.remove('is-selected'));
        choice.classList.add('is-selected');

        this.state.cardName = choice.dataset.card;
        this.state.addCard = (choice.dataset.card !== "No Card");
        this.updateTotals();
      });
    });

    // 3. Textarea Message character counter limit tracker
    const messageTextarea = this.querySelector('#byob-message');
    const charCounter = this.querySelector('#byob-char-counter');

    if (messageTextarea && charCounter) {
      messageTextarea.addEventListener('input', (e) => {
        this.state.message = e.target.value;
        charCounter.textContent = `${e.target.value.length} / 120`;
      });
    }

    // 4. Wrapping Checkbox toggle
    const wrapCheckbox = this.querySelector('#byob-add-wrap');
    if (wrapCheckbox) {
      wrapCheckbox.addEventListener('change', (e) => {
        this.state.addWrap = e.target.checked;
        this.updateTotals();
      });
    }
  }

  // Add Chiki to State
  addChikki(card) {
    if (!card) return;
    if (this.state.selectedItems.length >= this.state.boxCapacity) {
      alert(`Your box is full! Please choose a larger box size or remove items.`);
      return;
    }

    const itemData = {
      productId: card.dataset.productId,
      variantId: card.dataset.variantId,
      title: card.dataset.title,
      price: parseInt(card.dataset.price),
      image: card.dataset.image
    };

    this.state.selectedItems.push(itemData);
    this.syncQuantitiesToDOM();
    this.renderSlots();
    this.renderSidebarList();
    this.updateTotals();
    this.updateProgressTracker();
  }

  // Remove Chiki from State
  removeChikki(card) {
    if (!card) return;
    const variantId = card.dataset.variantId;
    this.removeChikkiByVariantId(variantId);
  }

  removeChikkiByVariantId(variantId) {
    const index = this.state.selectedItems.map(item => item.variantId).lastIndexOf(variantId);
    if (index > -1) {
      this.state.selectedItems.splice(index, 1);
    }
    this.state.isFullCelebrated = false; // Reset celebrations on subtraction
    this.syncQuantitiesToDOM();
    this.renderSlots();
    this.renderSidebarList();
    this.updateTotals();
    this.updateProgressTracker();
  }

  syncQuantitiesToDOM() {
    const cards = this.querySelectorAll('.chikki-card');
    cards.forEach(card => {
      const variantId = card.dataset.variantId;
      if (!variantId) return;

      const qty = this.state.selectedItems.filter(item => item.variantId === variantId).length;
      
      const qtyVal = card.querySelector('.chikki-qty-val');
      if (qtyVal) qtyVal.textContent = qty;

      const minusBtn = card.querySelector('.minus-btn');
      if (minusBtn) minusBtn.disabled = (qty === 0);

      const qtySelector = card.querySelector('.chikki-qty-selector');
      if (qtySelector) {
        if (qty > 0) {
          qtySelector.classList.add('has-qty');
        } else {
          qtySelector.classList.remove('has-qty');
        }
      }
    });
  }

  // Renders the segmented progress blocks inside summary card (Robust Inline Styles added)
  renderSlots() {
    const container = document.getElementById('byob-segments-container');
    if (!container) return;
    
    container.innerHTML = '';
    const count = this.state.selectedItems.length;

    for (let i = 0; i < this.state.boxCapacity; i++) {
      const segment = document.createElement('div');
      segment.classList.add('byob-segment');
      
      // Force inline style definitions to bypass any local CSS cache delays
      Object.assign(segment.style, {
        flex: '1 1 0%',
        height: '100%',
        borderRadius: '2px',
        minWidth: '5px',
        transition: 'background-color 0.2s ease-in-out'
      });

      if (i < count) {
        segment.style.backgroundColor = '#FF6B00'; // Active Orange
      } else {
        segment.style.backgroundColor = '#EADCC9'; // Empty Tan
      }

      container.appendChild(segment);
    }
  }

  // Renders summary rows in the sidebar
  renderSidebarList() {
    const container = document.getElementById('byob-sidebar-items-list');
    if (!container) return;
    
    container.innerHTML = '';

    const grouped = {};
    this.state.selectedItems.forEach(item => {
      if (!grouped[item.variantId]) {
        grouped[item.variantId] = {
          ...item,
          quantity: 0
        };
      }
      grouped[item.variantId].quantity += 1;
    });

    Object.values(grouped).forEach(item => {
      const row = document.createElement('div');
      row.classList.add('byob-sidebar-row');
      row.innerHTML = `
        <div class="byob-sidebar-row-left">
          <img src="${item.image}" alt="${item.title}" loading="lazy">
          <div>
            <span style="display:block; font-weight:bold;">${item.title}</span>
            <span style="font-size:1.1rem; color:rgba(59,36,24,0.6);">${this.formatMoney(item.price)} each</span>
          </div>
        </div>
        <div style="display:flex; align-items:center; gap: 1rem;">
          <span class="byob-sidebar-row-qty">× ${item.quantity}</span>
          <button class="byob-sidebar-row-remove" type="button" data-variant-id="${item.variantId}">
            <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
          </button>
        </div>
      `;

      row.querySelector('.byob-sidebar-row-remove').addEventListener('click', (e) => {
        const variantId = e.currentTarget.dataset.variantId;
        this.removeChikkiByVariantId(variantId);
      });

      container.appendChild(row);
    });
  }

  updateProgressTracker() {
    const step2 = this.querySelector('#step-node-2');
    const step3 = this.querySelector('#step-node-3');
    const step4 = this.querySelector('#step-node-4');

    const count = this.state.selectedItems.length;

    if (step2) {
      if (count > 0) {
        step2.classList.add('is-active');
      } else {
        step2.classList.remove('is-active');
      }
    }

    if (step3 && step4) {
      if (count === this.state.boxCapacity) {
        step3.classList.add('is-active');
        step4.classList.add('is-active');
      } else {
        step3.classList.remove('is-active');
        step4.classList.remove('is-active');
      }
    }
  }

  updateTotals() {
    const totalDisplay = this.querySelector('#byob-total-display');
    const totalContents = this.querySelector('#byob-total-contents');
    const totalCard = this.querySelector('#byob-total-card');
    const totalWrap = this.querySelector('#byob-total-wrap');

    const sidebarBoxName = this.querySelector('#byob-sidebar-boxname');
    const sidebarCapacity = this.querySelector('#byob-sidebar-capacity');
    const sidebarFraction = this.querySelector('#byob-sidebar-fraction');
    const btnText = this.querySelector('#byob-btn-text');
    const submitBtn = this.querySelector('#byob-submit-button');
    const statusMsgElement = this.querySelector('#byob-sidebar-status-msg');

    // Calculate Costs
    const chikkisCost = this.state.selectedItems.reduce((acc, item) => acc + item.price, 0);
    const cardCost = this.state.addCard ? this.greetingCardPrice : 0;
    const wrapCost = this.state.addWrap ? this.giftWrapPrice : 0;
    const totalInCents = chikkisCost + cardCost + wrapCost;

    if (totalContents) totalContents.textContent = this.formatMoney(chikkisCost);
    if (totalCard) totalCard.textContent = this.formatMoney(cardCost);
    if (totalWrap) totalWrap.textContent = this.formatMoney(wrapCost);
    if (totalDisplay) totalDisplay.textContent = this.formatMoney(totalInCents);

    const count = this.state.selectedItems.length;
    const remaining = this.state.boxCapacity - count;

    if (sidebarBoxName) sidebarBoxName.textContent = this.state.boxName.split(' (')[0];
    if (sidebarCapacity) {
      sidebarCapacity.textContent = `${this.state.boxCapacity} Pieces`;
    }
    if (sidebarFraction) sidebarFraction.textContent = `${count} / ${this.state.boxCapacity}`;

    // Dynamic gamified status messaging
    let statusMessage = "Start adding your favorite chikis! 😋";
    if (count > 0 && count < Math.floor(this.state.boxCapacity / 2)) {
      statusMessage = "Off to a great start! Add some more 🚀";
    } else if (count >= Math.floor(this.state.boxCapacity / 2) && remaining > 1) {
      statusMessage = "Halfway there! Keep going! 🎉";
    } else if (remaining === 1) {
      statusMessage = "Almost there! Just 1 more to go! 😍";
    } else if (remaining === 0) {
      statusMessage = "Your box is complete and ready to gift! 🎁✨";
    }

    if (statusMsgElement) {
      statusMsgElement.textContent = statusMessage;
    }

    if (remaining > 0) {
      if (btnText) btnText.textContent = `Add ${remaining} More Item(s)`;
      if (submitBtn) submitBtn.disabled = true;
    } else {
      if (btnText) btnText.textContent = `Add Custom Box to Cart`;
      if (submitBtn) submitBtn.disabled = false;

      if (!this.state.isFullCelebrated) {
        this.state.isFullCelebrated = true;
        this.triggerCelebration();
      }
    }
  }

  triggerCelebration() {
    const sidebar = this.querySelector('.byob-sidebar');
    if (sidebar) {
      sidebar.classList.add('byob-shake');
      setTimeout(() => sidebar.classList.remove('byob-shake'), 600);
    }

    const particleColors = ['#FF6B00', '#3B2418', '#FFD700', '#FF4E00', '#FFBB00'];
    const emitterContainer = document.createElement('div');
    
    Object.assign(emitterContainer.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: '99999'
    });
    document.body.appendChild(emitterContainer);

    for (let i = 0; i < 60; i++) {
      const particle = document.createElement('div');
      
      Object.assign(particle.style, {
        position: 'absolute',
        width: '10px',
        height: '10px',
        backgroundColor: particleColors[Math.floor(Math.random() * particleColors.length)],
        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
        left: '50%',
        top: '50%',
        opacity: '1'
      });
      emitterContainer.appendChild(particle);

      const angle = Math.random() * Math.PI * 2;
      const force = 6 + Math.random() * 14;
      const xVel = Math.cos(angle) * force;
      const yVel = Math.sin(angle) * force;

      let pX = window.innerWidth / 2;
      let pY = window.innerHeight / 2;
      let pOpacity = 1;

      const runConfetti = () => {
        pX += xVel;
        pY += yVel + 0.4;
        pOpacity -= 0.025;

        particle.style.left = pX + 'px';
        particle.style.top = pY + 'px';
        particle.style.opacity = pOpacity;

        if (pOpacity > 0) {
          requestAnimationFrame(runConfetti);
        } else {
          particle.remove();
        }
      };
      requestAnimationFrame(runConfetti);
    }

    setTimeout(() => emitterContainer.remove(), 2500);

    setTimeout(() => {
      this.smoothScrollToStep(3);
    }, 900);
  }

  smoothScrollToStep(stepNumber) {
    const headings = this.querySelectorAll('.byob-step-heading');
    const heading = headings[stepNumber - 1];
    if (!heading) return;

    const stickyHeaderOffset = 210;
    const headingPosition = heading.getBoundingClientRect().top;
    const offsetPosition = headingPosition + window.pageYOffset - stickyHeaderOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  setupFormSubmission() {
    const handleAdd = async (btnElement) => {
      btnElement.disabled = true;

      // Show full-screen UI blocking overlay loader
      const overlay = document.getElementById('byob-loading-overlay');
      if (overlay) {
        overlay.classList.add('is-visible');
      }

      const boxVariantId = this.getBoxVariantId();
      if (!boxVariantId) {
        alert('Configuration error: Selected box size product is missing in theme settings.');
        btnElement.disabled = false;
        if (overlay) overlay.classList.remove('is-visible');
        return;
      }

      const byobUniqueGroupId = 'byob_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
      const cartItems = [];

      // Add the chosen specific size Packaging Product (instead of a generic master product)
      cartItems.push({
        id: parseInt(boxVariantId),
        quantity: 1,
        properties: {
          "_byob_id": byobUniqueGroupId,
          "Box Size": this.state.boxName,
          "Occasion": this.state.occasion,
          "Personal Message": this.state.message || "None",
          "Greeting Card Added": this.state.cardName,
          "Gift Wrap Added": this.state.addWrap ? "Yes" : "No"
        }
      });

      const variantCounts = {};
      this.state.selectedItems.forEach(item => {
        variantCounts[item.variantId] = (variantCounts[item.variantId] || 0) + 1;
      });

      for (const [variantId, quantity] of Object.entries(variantCounts)) {
        cartItems.push({
          id: parseInt(variantId),
          quantity: quantity,
          properties: {
            "_byob_id": byobUniqueGroupId,
            "Box Unit": this.state.boxName
          }
        });
      }

      if (this.state.addCard && this.greetingCardVariantId) {
        cartItems.push({
          id: parseInt(this.greetingCardVariantId),
          quantity: 1,
          properties: { "_byob_id": byobUniqueGroupId }
        });
      }
      if (this.state.addWrap && this.giftWrapVariantId) {
        cartItems.push({
          id: parseInt(this.giftWrapVariantId),
          quantity: 1,
          properties: { "_byob_id": byobUniqueGroupId }
        });
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });

        if (response.ok) {
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          window.location.href = '/cart';
        } else {
          const errData = await response.json();
          alert('Error: ' + errData.description);
          btnElement.disabled = false;
          if (overlay) overlay.classList.remove('is-visible');
          this.updateTotals();
        }
      } catch (err) {
        console.error(err);
        alert('An error occurred. Please try again.');
        btnElement.disabled = false;
        if (overlay) overlay.classList.remove('is-visible');
        this.updateTotals();
      }
    };

    const submitBtn = this.querySelector('#byob-submit-button');
    if (submitBtn) {
      submitBtn.addEventListener('click', (e) => {
        handleAdd(e.currentTarget);
      });
    }
  }
}

customElements.define('byob-builder', BYOBBuilder);